import React, { useState, useMemo } from "react";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { REPORT_CENTER, REPORT_AUDIT_LOG, reportToCsv } from "../lib/dataReports";
import { REPORT_ACCESS } from "../lib/users";
import { useRole } from "../lib/role.jsx";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import { useCopilot } from "../lib/copilot.jsx";
import {
  FileText, Sparkles, Download, Mail, Play, Clock, ShieldCheck,
  ArrowLeft, MessageSquare, Calendar, Users, History, ChevronRight,
} from "lucide-react";

function fmtCell(value, format) {
  if (value === null || value === undefined) return "—";
  if (format === "peso") return formatPeso(value, { compact: true });
  if (format === "pct") return pct(value);
  if (typeof value === "number") return formatNumber(value);
  return value;
}

function fmtKpi(v, f) {
  if (typeof v !== "number") return v;
  if (f === "peso") return formatPeso(v, { compact: true });
  if (f === "pct") return pct(v);
  return formatNumber(v);
}

function downloadCsv(report) {
  const csv = reportToCsv(report);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${report.id}-${report.period.replace(/[^A-Za-z0-9]+/g, "_")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ReportsHub() {
  const { user, role } = useRole();
  const [selectedId, setSelectedId] = useState(null);
  const [toast, setToast] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [lastRuns, setLastRuns] = useState({});
  const [auditLog, setAuditLog] = useState(REPORT_AUDIT_LOG);
  const toastTimer = React.useRef(null);
  const genTimer = React.useRef(null);

  // Filter reports based on role-scoped access; null = all reports.
  const allowedIds = user ? REPORT_ACCESS[user.role] : null;
  const reports = useMemo(() => (
    allowedIds === null || !allowedIds ? REPORT_CENTER : REPORT_CENTER.filter(r => allowedIds.includes(r.id))
  ), [allowedIds]);
  const isScoped = allowedIds !== null && allowedIds;
  // Filter audit log to the visible reports for role-scoped users, otherwise show all.
  const visibleAuditLog = useMemo(() => (
    !isScoped ? auditLog : auditLog.filter(e => reports.some(r => r.name === e.report))
  ), [auditLog, reports, isScoped]);

  React.useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    if (genTimer.current) clearTimeout(genTimer.current);
  }, []);

  function flash(msg) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  }

  function pushAudit(report, action, detail) {
    const ts = new Date().toISOString().slice(0, 16).replace("T", " ");
    setAuditLog(prev => [{ ts, report: report.name, actor: "danny@asterisk.com.sg", action, detail }, ...prev].slice(0, 12));
  }

  function generate(report) {
    setRunningId(report.id);
    if (genTimer.current) clearTimeout(genTimer.current);
    genTimer.current = setTimeout(() => {
      const now = new Date();
      const stamp = now.toISOString().slice(0, 16).replace("T", " ");
      setLastRuns(prev => ({ ...prev, [report.id]: stamp }));
      setRunningId(null);
      flash(`${report.name} regenerated · ${report.rowCount} rows · ready to deliver`);
      pushAudit(report, "Manual re-run", `${report.rowCount} rows refreshed · triggered by CFO Office`);
    }, 1100);
  }

  function emailMe(report) {
    flash(`${report.name} queued to email · audience: ${report.audience.join(", ")}`);
    pushAudit(report, "Email queued", `Delivery to ${report.audience.length} audience groups via ${report.deliveryChannels.join(" + ")}`);
  }

  function handleCsv(report) {
    downloadCsv(report);
    pushAudit(report, "CSV exported", `Local download · ${report.rowCount} rows · ${report.columns.length} columns`);
  }

  if (selectedId) {
    const report = REPORT_CENTER.find(r => r.id === selectedId);
    return (
      <ReportDetail
        report={report}
        lastRunOverride={lastRuns[report.id]}
        onBack={() => setSelectedId(null)}
        onGenerate={() => generate(report)}
        onEmail={() => emailMe(report)}
        onDownload={() => handleCsv(report)}
        running={runningId === report.id}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART IV · Finance — Automated Reporting"
        title="Automated Reports Center"
        description="Standard executive reports auto-generated on schedule by the Fabric pipeline — with AI executive summaries, full row preview, CSV export, and email delivery." />

      {isScoped && (
        <div data-testid="reports-scope-banner" className="tile p-3 flex items-center gap-2.5 text-[12px] border-l-2 border-primary/60 bg-primary/5">
          <Badge status="info">Role-scoped</Badge>
          <span className="text-fg/90">Showing {reports.length} of {REPORT_CENTER.length} reports — SOW Lite Pilot D5 deliverables for <span className="font-medium">{role?.name}</span>.</span>
        </div>
      )}

      {/* Hub stats strip */}
      <div className="tile grid grid-cols-2 md:grid-cols-4 divide-x divide-border" data-testid="reports-stats">
        <Stat label="Active Reports" value={reports.length} />
        <Stat label="Audiences served" value={Array.from(new Set(reports.flatMap(r => r.audience))).length} />
        <Stat label="Delivery Channels" value={Array.from(new Set(reports.flatMap(r => r.deliveryChannels))).length} />
        <Stat label="Avg AI Confidence" value={pct(reports.reduce((s, r) => s + r.confidence, 0) / (reports.length || 1))} status="success" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {reports.map(r => {
          const last = lastRuns[r.id] || r.lastRun;
          return (
            <Tile key={r.id} className="col-span-12 md:col-span-6" testid={`report-card-${r.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-display text-base font-semibold tracking-tight">{r.name}</h3>
                    <Badge status={r.status === "ready" ? "success" : "warning"}>{r.status === "ready" ? "Ready" : "Draft"}</Badge>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-muted flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar size={10} />{r.cadence}</span>
                    <span className="inline-flex items-center gap-1"><Users size={10} />{r.audience.length} audiences</span>
                  </div>
                  <p className="text-[13px] text-fg/90 mt-2 line-clamp-3">{r.aiExec}</p>

                  <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
                    <div className="flex items-center justify-between"><span className="text-muted">Period</span><span className="font-mono-tabular">{r.period}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted">Rows</span><span className="font-mono-tabular">{r.rowCount}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted inline-flex items-center gap-1"><Clock size={10} />Last run</span><span className="font-mono-tabular">{last}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted">Next run</span><span className="font-mono-tabular">{r.nextRun}</span></div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button data-testid={`report-open-${r.id}`} onClick={() => setSelectedId(r.id)}
                      className="px-3 py-1.5 text-[11px] bg-primary text-primary-foreground hover:opacity-90 rounded-sm inline-flex items-center gap-1">
                      Open <ChevronRight size={12} />
                    </button>
                    <button data-testid={`report-generate-${r.id}`} onClick={() => generate(r)} disabled={runningId === r.id}
                      className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1 disabled:opacity-60">
                      <Play size={11} />{runningId === r.id ? "Generating…" : "Generate now"}
                    </button>
                    <button data-testid={`report-download-${r.id}`} onClick={() => handleCsv(r)}
                      className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1">
                      <Download size={11} /> CSV
                    </button>
                    <button data-testid={`report-email-${r.id}`} onClick={() => emailMe(r)}
                      className="px-3 py-1.5 text-[11px] border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1">
                      <Mail size={11} /> Email me
                    </button>
                  </div>
                </div>
              </div>
            </Tile>
          );
        })}
      </div>

      {/* Audit log */}
      <Tile title="Report Audit Log" testid="report-audit" action={<Badge status="info"><History size={10} className="inline mr-1" />{visibleAuditLog.length} entries</Badge>}>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted">
                <th className="text-left py-2 px-3 border-b border-border">Timestamp</th>
                <th className="text-left py-2 px-3 border-b border-border">Report</th>
                <th className="text-left py-2 px-3 border-b border-border">Actor</th>
                <th className="text-left py-2 px-3 border-b border-border">Action</th>
                <th className="text-left py-2 px-3 border-b border-border">Detail</th>
              </tr>
            </thead>
            <tbody>
              {visibleAuditLog.map((e, i) => (
                <tr key={i} className="border-b border-border last:border-b-0 hover:bg-surface-2">
                  <td className="py-2 px-3 font-mono-tabular text-muted">{e.ts}</td>
                  <td className="py-2 px-3 font-medium">{e.report}</td>
                  <td className="py-2 px-3 text-muted">{e.actor}</td>
                  <td className="py-2 px-3"><Badge status={e.action.includes("Auto") ? "info" : e.action.includes("Manual") ? "warning" : "success"}>{e.action}</Badge></td>
                  <td className="py-2 px-3 text-fg/85">{e.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Tile>

      {/* Toast */}
      {toast && (
        <div data-testid="report-toast" className="fixed bottom-6 right-6 z-50 bg-surface border border-border-strong rounded-sm shadow-lg px-4 py-3 flex items-center gap-2 text-sm">
          <ShieldCheck size={14} className="text-success" /> {toast}
        </div>
      )}
    </div>
  );
}

function ReportDetail({ report, lastRunOverride, onBack, onGenerate, onEmail, onDownload, running }) {
  const { askCopilot } = useCopilot();
  const lastRun = lastRunOverride || report.lastRun;

  const totals = useMemo(() => {
    // Sum numeric columns for totals row (excluding ratio columns)
    const ratioKeys = new Set(["grossMargin", "achievement", "overstockShare", "ebitdaMargin", "onTimeDelivery", "leadTimeAccuracy", "costVariance", "qcReject", "turnover"]);
    const out = {};
    report.columnKeys.forEach(k => {
      if (k === report.columnKeys[0]) { out[k] = "Total"; return; }
      if (ratioKeys.has(k)) {
        const vals = report.rows.map(r => r[k]).filter(v => typeof v === "number");
        out[k] = vals.length ? +(vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(3) : "—";
      } else {
        out[k] = report.rows.reduce((s, r) => s + (typeof r[k] === "number" ? r[k] : 0), 0);
      }
    });
    return out;
  }, [report]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={onBack} data-testid="report-back"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted hover:text-primary mb-2">
            <ArrowLeft size={12} /> Back to Reports Center
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-1">Automated Report · {report.cadence}</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{report.name}</h1>
          <p className="text-muted text-sm mt-1.5">Period: <span className="font-mono-tabular text-fg/90">{report.period}</span> · Last run: <span className="font-mono-tabular text-fg/90">{lastRun}</span> · Owner: {report.owner}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="report-detail-generate" onClick={onGenerate} disabled={running}
            className="px-3 py-2 text-xs bg-primary text-primary-foreground hover:opacity-90 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Play size={12} /> {running ? "Generating…" : "Generate now"}
          </button>
          <button data-testid="report-detail-download" onClick={onDownload}
            className="px-3 py-2 text-xs border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1.5">
            <Download size={12} /> Download CSV
          </button>
          <button data-testid="report-detail-email" onClick={onEmail}
            className="px-3 py-2 text-xs border border-border hover:border-primary hover:text-primary rounded-sm inline-flex items-center gap-1.5">
            <Mail size={12} /> Email me
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="tile grid grid-cols-2 md:grid-cols-4 divide-x divide-border" data-testid="report-kpis">
        {report.kpis.map((k, i) => (
          <div key={i} className="px-4 py-3">
            <div className="kpi-label flex items-center gap-1.5">{k.status && <StatusDot status={k.status} />}{k.label}</div>
            <div className="kpi-value text-xl mt-1.5">{fmtKpi(k.value, k.fmt)}</div>
            {typeof k.delta === "number" && (
              <div className={cn("text-[10px] font-mono-tabular mt-1", k.delta >= 0 ? "text-success" : "text-critical")}>
                {k.delta >= 0 ? "+" : ""}{(k.delta * 100).toFixed(1)}% vs prior
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* AI executive summary */}
        <Tile className="col-span-12 lg:col-span-7" testid="report-ai-summary"
              title="AI Executive Summary"
              action={<Badge status="info">Confidence {(report.confidence * 100).toFixed(0)}%</Badge>}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Sparkles size={16} />
            </div>
            <div className="flex-1">
              <p className="text-[13px] text-fg/95 leading-relaxed">{report.aiExec}</p>
              <button data-testid="ask-copilot-report"
                onClick={() => askCopilot(
                  `As CEO, brief me on the latest "${report.name}" (period ${report.period}). AI summary: ${report.aiExec} Key KPIs: ${report.kpis.map(k => `${k.label} = ${fmtKpi(k.value, k.fmt)}${typeof k.delta === "number" ? ` (${k.delta >= 0 ? "+" : ""}${(k.delta * 100).toFixed(1)}% vs prior)` : ""}`).join("; ")}. Walk me through: (1) the 3 things I must act on this week, (2) which downside risks are not in the summary, (3) which other modules in the Control Tower I should cross-check (Branch Performance, Inventory, Procurement, etc.), and (4) what to put on the board agenda.`
                )}
                className="mt-3 inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium">
                <MessageSquare size={11} /> Ask Copilot for the full briefing →
              </button>
            </div>
          </div>
        </Tile>

        {/* Delivery + Audience */}
        <Tile className="col-span-12 lg:col-span-5" testid="report-delivery" title="Delivery & Audience">
          <div className="space-y-3 text-[12px]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1.5">Audience</div>
              <div className="flex flex-wrap gap-1.5">
                {report.audience.map((a, i) => (
                  <span key={i} className="px-2 py-1 border border-border rounded-sm font-medium">{a}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1.5">Delivery channels</div>
              <div className="flex flex-wrap gap-1.5">
                {report.deliveryChannels.map((c, i) => (
                  <span key={i} className="px-2 py-1 bg-surface-2 rounded-sm font-medium inline-flex items-center gap-1"><Mail size={10} />{c}</span>
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-border grid grid-cols-2 gap-2 text-[11px]">
              <div><div className="text-muted">Cadence</div><div className="font-mono-tabular mt-0.5">{report.cadence}</div></div>
              <div><div className="text-muted">Next run</div><div className="font-mono-tabular mt-0.5">{report.nextRun}</div></div>
              <div><div className="text-muted">Owner</div><div className="font-mono-tabular mt-0.5">{report.owner}</div></div>
              <div><div className="text-muted">Status</div><div className="mt-0.5"><Badge status={report.status === "ready" ? "success" : "warning"}>{report.status === "ready" ? "Ready" : "Draft"}</Badge></div></div>
            </div>
          </div>
        </Tile>

        {/* Full row preview */}
        <Tile className="col-span-12" testid="report-rows"
              title={`Full Row Preview — ${report.rows.length} of ${report.rowCount}`}
              action={<span className="text-[10px] uppercase tracking-widest text-muted">Sample · representative</span>}>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted">
                  {report.columns.map((c, i) => (
                    <th key={i} className={cn("py-2 px-3 border-b border-border", i === 0 ? "text-left" : "text-right")}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-border last:border-b-0 hover:bg-surface-2" data-testid={`report-row-${ri}`}>
                    {report.columnKeys.map((k, ci) => (
                      <td key={k} className={cn("py-2 px-3", ci === 0 ? "text-left font-medium" : "text-right font-mono-tabular")}>
                        {fmtCell(row[k], report.formats?.[k])}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-surface-2 font-semibold" data-testid="report-totals">
                  {report.columnKeys.map((k, ci) => (
                    <td key={k} className={cn("py-2 px-3", ci === 0 ? "text-left" : "text-right font-mono-tabular")}>
                      {ci === 0 ? "Totals / Avg" : fmtCell(totals[k], report.formats?.[k])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Tile>
      </div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-xl mt-1.5">{value}</div>
    </div>
  );
}
